<template>
  <lb-dialog
    v-model="internalValue"
    heading="Bulk Approve/Reject Commentary"
    :width="width"
    :loading="loading"
  >
    <template v-slot:body>
      <div class="mb-4">
        <v-btn color="primary" class="mb-3" @click="handleDownloadTemplate"><span>Download Template</span></v-btn>
        
        <lb-file
          v-model="fileModel"
          :label="hasFileData ? 'Re-Upload' : 'Browse Files'"
          :drag="false"
          hidedetails
          @change="handleFileChange"
          :displayname="true"
          accept=".xlsx,.xls,.xlsm,.xlsb"
          :dropzone="true"
          dropzone-text="Drag and drop your Excel file here"
          dropzone-hint="Supported formats: .xlsx, .xls, .xlsm, .xlsb"
          :remove="true"
        />
      </div>
      <div v-if="hasFileData">
        <div v-if="hasError" class="error--text font-weight-semibold">
          We found few errors. Please rectify the same to proceed with the upload
        </div>
        <v-simple-table dense>
          <thead class="tableHeaderBg">
            <tr>
              <th scope="flag"></th>
              <th scope="glcode">{{$nova.labelsName('cycle_page','commentary_gl_code_label','GL Code')}}</th>
              <th scope="glcode">GL Description</th>
              <th scope="commentary1">Commentary 1</th>
              <th scope="commentary2">Commentary 2</th>
              <th scope="action">Action</th>
              <th scope="rejectLevel" v-if="isRegionalApprover">Reject - Local (LA/LP)</th>
              <th scope="reason">Reason</th>
              <th scope="commentary2">Message</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in pagedItems"
              :key="item.__key || idx"
              class="hoverable"
            >
              <td style="width:30px;">
                <v-icon v-if="item.error || !item.__found" color="error">mdi-close-circle</v-icon>
                <v-icon v-else-if="item.ignore === false" color="success">mdi-check-circle</v-icon>
                <v-icon v-else color="warning">mdi-alert</v-icon>
              </td>
              <td>{{ item.code }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.commentary1 || '' }}</td>
              <td>{{ item.commentary2 || '' }}</td>
              <td>
                <v-chip small label color="success" v-if="showApproveChip(item)">
                  <span>{{ approveLabel }}</span>
                </v-chip>
                <v-chip small label color="error" v-if="showRejectChip(item)"><span>Reject</span></v-chip>
              </td>
              <td v-if="isRegionalApprover">
                <template v-if="showRejectChip(item) && item.rejection_target">
                  <v-chip small label color="warning" v-if="item.rejection_target === 'la'">
                    <span>Local Approver (LA)</span>
                  </v-chip>
                  <v-chip small label  color="info"  v-if="item.rejection_target === 'lp'">
                    <span>Local Preparer (LP)</span>
                  </v-chip>
                </template>
              </td>
              <td>{{ item.approvereason || '' }}</td>
              <td :class="messageClass(item)">
                <div v-if="!item.__found" class="d-flex align-center">
                  <v-icon small color="error" class="mr-1">mdi-alert</v-icon>
                  {{$nova.labelsName('cycle_page','commentary_gl_code_label','GL Code')}} not found
                </div>
                <div v-else-if="item.error" class="d-flex align-center">
                  <v-icon small color="error" class="mr-1">mdi-alert</v-icon>
                  {{ item.error }}
                </div>
                <div v-else-if="item.hasWarning" class="d-flex align-center">
                  <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
                  {{ item.warning }}
                </div>
                <div v-else-if="item.ignore" class="d-flex align-center">
                  <v-icon small color="warning" class="mr-1">mdi-information</v-icon>
                  {{ item.message }}
                </div>
                <div v-else class="d-flex align-center">
                  <v-icon small color="success" class="mr-1">mdi-check</v-icon>
                  {{ item.message }}
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <!-- Pagination -->
        <div class="d-flex justify-end pt-2" v-if="pageCount > 1">
          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="7"
          ></v-pagination>
        </div>
      </div>
    </template>
    <template v-slot:actions>
      <div v-if="hasFileData" class="info--text">
        Only GLs in pending approval status will be updated
      </div>
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasFileData && !hasError"
        @click="handleUpload()"
        color="primary"
        small
        class="ml-2"
      >
        <span>Proceed to Upload</span>
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
import { BULK_APPROVE_REJECT } from '../utils/constants';

export default {
  name: 'BulkApproveLocalDialog',
  props: {
    value: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    width: { type: [String, Number], default: 500 },
    fileDataList: { type: [Array, Object], default: () => [] },
    hasError: { type: Boolean, default: false },
    isRegionalCommentaryAutomationEnabled: { type: Boolean, default: false },
    isApprover: { type: Boolean, default: false },
    isRegionalApprover: { type: Boolean, default: false },
    fileValue: { type: [File, Object, null], default: null },
  },

  data() {
    return {
      page: 1,
      perPage: 10,
    };
  },

  watch: {
    fileDataList() {
      // Reset page when new file is uploaded
      this.page = 1;
    },
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    fileModel: {
      get() {
        return this.fileValue;
      },
      set(value) {
        this.$emit('update:file-value', value);
      },
    },
    processedItems() {
      if (Array.isArray(this.fileDataList)) {
        return this.fileDataList;
      }
      if (this.fileDataList && typeof this.fileDataList === 'object') {
        return Object.keys(this.fileDataList).map(key => ({
          __key: key,
          ...this.fileDataList[key],
        }));
      }
      return [];
    },
    hasFileData() {
      return this.processedItems.length > 0;
    },
    pageCount() {
      return Math.ceil(this.processedItems.length / this.perPage);
    },
    pagedItems() {
      const start = (this.page - 1) * this.perPage;
      const end = start + this.perPage;
      return this.processedItems.slice(start, end);
    },
    approveLabel() {
      return this.isRegionalCommentaryAutomationEnabled && this.isApprover
        ? BULK_APPROVE_REJECT.APPROVE_TYPES.APPROVE_AND_SUBMIT
        : BULK_APPROVE_REJECT.APPROVE_TYPES.APPROVE;
    },
  },
  methods: {
    handleDownloadTemplate() {
      this.$emit('download-template');
    },
    handleFileChange(event) {
      const file = event?.target?.files?.[0] || event || null;
      this.$emit('update:file-value', file);
      this.$emit('file-change', event);
    },
    handleUpload() {
      this.$emit('upload');
    },
    showApproveChip(item) {
      return item.stage_local === 2 || item.stage_regional === 2;
    },
    showRejectChip(item) {
      return item.stage_local === -1 || item.stage_regional === -1;
    },
    messageClass(item) {
      return {
        'error--text': item.error || item.ignore || item.hasWarning,
        'success--text': !item.error && !item.ignore && !item.hasWarning,
      };
    },
  },
};
</script>
